import { type FormEvent, type FunctionComponent, type KeyboardEvent, useRef } from "react";

const VIN_LENGTH = 17;
const VIN_PLACEHOLDER = "VF7VEAHXHHZ088189";
const FORBIDDEN_CHARACTERS = ["I", "O", "Q"];

type VinInputProps = {
  invalid?: boolean;
  setShowHint?: (showHint: boolean) => void;
  vin: string | null;
  setVin: (value: string) => void;
  required?: boolean;
};

/**
 * Splits a VIN into one field per character. A VIN never contains I, O or Q, so these characters
 * call `setShowHint(true)` instead of `setVin`.
 */
export const VinInput: FunctionComponent<VinInputProps> = ({
  invalid = false,
  setShowHint,
  vin,
  setVin,
  required = false,
}: VinInputProps) => {
  const vinArray = (vin ?? "").split("").concat(Array(VIN_LENGTH - (vin?.length ?? 0)).fill(""));
  const hasInput = vinArray.some((character) => character !== "");
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const setVinFromArray = (characters: string[]) => setVin(characters.join("").trim());

  const handleInput = (e: FormEvent<HTMLInputElement>, index: number) => {
    const target = e.target as HTMLInputElement;
    const character = target.value.toUpperCase();

    if (FORBIDDEN_CHARACTERS.includes(character)) {
      setShowHint?.(true);
      target.value = "";
      return;
    }

    setShowHint?.(false);

    if (!/^[A-Z0-9]$/.test(character)) {
      target.value = "";
      return;
    }

    const newVin = [...vinArray];
    newVin[index] = character;
    setVinFromArray(newVin);

    if (index < vinArray.length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key !== "Backspace") {
      return;
    }

    e.preventDefault();
    const newVin = [...vinArray];
    if (vinArray[index] === "" && index > 0) {
      inputRefs.current[index - 1]?.focus();
      newVin[index - 1] = "";
    } else {
      newVin[index] = "";
    }
    setVinFromArray(newVin);
  };

  return (
    <div
      className={`flex h-fit w-full gap-1 overflow-x-auto ${invalid ? "text-red-500" : "text-gray-700"}`}
      style={{ padding: "0.2rem 0.2rem 0.5rem" }}
    >
      {vinArray.map((character, index) => (
        <input
          ref={(element) => {
            if (element) {
              inputRefs.current[index] = element;
            }
          }}
          required={required}
          key={index}
          maxLength={1}
          className={`h-10 flex-1 w-1 rounded-md bg-gray-100 border ${invalid ? "border-red-200" : "border-gray-200"} text-center sm:h-12`}
          value={character}
          onInput={(e) => handleInput(e, index)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          placeholder={hasInput ? undefined : VIN_PLACEHOLDER[index]}
          style={{ userSelect: "none" }}
        />
      ))}
    </div>
  );
};
